import React from 'react';
import { Input } from '../ui/input';
import { toProperCase } from '../../lib/utils';

/**
 * Single-line-item editor row for the Sales Order "Edit / Amend" table.
 * Extracted from EditOrderModal so the parent stays readable and this row's
 * DOM structure can be reasoned about in isolation.
 *
 * Renders two <tr>s:
 *   1. Main fields — one <td> per MAIN_COLS entry + a computed Total cell.
 *   2. Secondary fields — a flex-wrap strip spanning all columns (grade,
 *      finish, lead times, tooling costs, remarks…).
 */
export function LineItemEditorRow({
  item, idx, mainCols, subFields, updateEditItem, formatCurrency, lineTotal,
}) {
  return (
    <React.Fragment>
      <tr className="border-t">
        <td className="px-2 pt-2 text-slate-500 font-medium align-middle">{idx + 1}</td>
        {mainCols.map(c => (
          <td key={c.key} className="px-1 pt-2 align-middle">
            <Input
              type={c.type}
              step={c.step}
              min={c.type === 'number' ? '0' : undefined}
              value={item[c.key] ?? ''}
              onChange={(e) => updateEditItem(idx, c.key, c.numeric ? (parseFloat(e.target.value) || 0) : e.target.value)}
              onBlur={c.properCase ? (e) => e.target.value && updateEditItem(idx, c.key, toProperCase(e.target.value)) : undefined}
              className={`h-8 text-sm px-2 ${c.width} ${c.align || ''} ${c.mono ? 'font-mono' : ''}`}
              data-testid={`edit-item-${c.key.replace(/_/g, '-')}-${idx}`}
            />
          </td>
        ))}
        <td className="px-2 pt-2 text-right font-semibold whitespace-nowrap align-middle" data-testid={`edit-item-total-${idx}`}>
          {formatCurrency(lineTotal(item))}
        </td>
      </tr>
      <tr className="border-b border-slate-100">
        <td />
        <td colSpan={mainCols.length + 1} className="px-1 pt-1.5 pb-2.5">
          <div className="flex flex-wrap items-end gap-x-3 gap-y-1.5">
            {subFields.map(f => (
              <div key={f.key} className={f.width}>
                <span className="block text-[10px] uppercase tracking-wide text-slate-400">{f.label}</span>
                <Input
                  type={f.type || 'text'}
                  step={f.step}
                  min={f.type === 'number' ? '0' : undefined}
                  value={item[f.key] ?? ''}
                  onChange={(e) => updateEditItem(idx, f.key, e.target.value)}
                  className="h-7 text-xs px-2 bg-slate-50"
                  data-testid={`edit-item-${f.key.replace(/_/g, '-')}-${idx}`}
                />
              </div>
            ))}
          </div>
        </td>
      </tr>
    </React.Fragment>
  );
}
